import { apiSlice } from './apiSlice'
import { auth, functions } from '../firebase'
import { EmailAuthProvider, reauthenticateWithCredential, signInWithEmailAndPassword, signOut, sendEmailVerification } from 'firebase/auth'
import { logOut } from './authSlice'
import { httpsCallable } from 'firebase/functions'

// Helper type for error responses
interface FirebaseError {
  code?: string
  message: string
}

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({

    // Create account endpoint
    createAccount: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, "createAccount");
          const result = await callable(data)

          await signInWithEmailAndPassword(auth, data.email, data.password)

          if (auth.currentUser) {
            await sendEmailVerification(auth.currentUser)
          }

          return { data: result.data };
        } catch (error) {
          return {
            error: {
              status: error.code,
              data: {
                message: error.message,
              },
            },
          };
        }
      },
    }),

    // Log in endpoint using Firebase SDK
    loginUser: builder.mutation({
      async queryFn({ email, password }) {
        try {
          await signInWithEmailAndPassword(auth, email, password)

          return { data: 'Logged in successfully' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
    }),

    // Log out endpoint using Firebase SDK
    logoutUser: builder.mutation({
      async queryFn(_, { dispatch }) {
        try {
          await signOut(auth)

          dispatch(apiSlice.util.resetApiState())
          dispatch(logOut())

          return { data: 'Logged out successfully' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      }
    }),

    // Delete user
    deleteUser: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'deleteUser')
          const response = await callable(data)
          
          return { data: response.data }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          // Delete the user from the cache
          dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData('fetchUsers', undefined, (draft: any) => {
              if (draft) {
                delete draft[id];
              }
            })
          )
        } catch (error) {
        }
      },
      invalidatesTags: (result: any, error, arg) => {
        if (error || !result?.clubIds?.length) {
          return []
        }

        return [
          { type: 'Clubs' },
          ...result.clubIds.map((clubId: string) => ({ type: 'ClubMembers', id: clubId }))
        ]
      }
    }),

    checkEmailExists: builder.mutation({
      async queryFn(email) {
        if (!email) {
          throw new Error(
            'Email is required for email existence check.'
          )
        }

        try {
          const callable = httpsCallable(functions, 'checkEmailExists')
          const response = await callable({ email })

          return { data: response.data }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      }
    }),

    reauthenticateUser: builder.mutation({
      async queryFn({ email, password }) {
        try {
          const user = auth.currentUser

          if (!user) {
            throw new Error("User not logged in")
          }

          const credential = EmailAuthProvider.credential(email, password)

          await reauthenticateWithCredential(user, credential)

          return { data: 'Reauthenticated successfully' }

        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      }
    })
  }),
})

export const {
  useCreateAccountMutation,
  useLoginUserMutation,
  useLogoutUserMutation,
  useDeleteUserMutation,
  useCheckEmailExistsMutation,
  useReauthenticateUserMutation
} = authApiSlice